import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Divider,
  Alert,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import { Button, GridLayout, Text } from '@qwickapps/react-framework';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import { useMiddlewareClient, type Organization } from '@qwickforge/react-client';

export default function OrganizationSettings() {
  const client = useMiddlewareClient();
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    website: '',
    industry: '',
    size: '',
  });
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOrganization();
  }, [client]);

  const loadOrganization = async () => {
    if (!client) return;

    try {
      setLoading(true);
      setError('');
      const data = await client.organizations.getCurrent();
      setOrganization(data);
      setFormData({
        name: data.name || '',
        slug: data.slug || '',
        description: data.description || '',
        website: data.website || '',
        industry: data.industry || '',
        size: data.size || '',
      });
    } catch (err: any) {
      console.error('Failed to load organization:', err);
      setError(err.message || 'Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // TODO: Implement organization update API call
      console.log('Saving organization:', formData);
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Failed to save organization:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Text variant="h6" gutterBottom customColor="var(--theme-accent-dark)">
        Organization Settings
      </Text>
      <Text variant="body2" style={{ marginBottom: '24px' }} customColor="var(--theme-text)">
        Manage your organization profile and settings
      </Text>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {organization?.isPersonal && (
        <Alert severity="info" icon={<BusinessIcon sx={{ color: 'var(--theme-primary)' }} />} sx={{ mb: 3 }}>
          You are on the Free plan with a personal organization. Upgrade to Team or Enterprise to unlock organization features and collaborate with your team.
        </Alert>
      )}

      {loading ? (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <GridLayout columns={2} spacing="medium">
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={100} data-grid-span={12} />
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={56} />
            </GridLayout>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <GridLayout columns={2} spacing="medium">
                <TextField
                  fullWidth
                  label="Organization Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  placeholder="Acme Inc."
                  disabled={organization?.isPersonal}
                />
                <TextField
                  fullWidth
                  label="URL Slug"
                  value={formData.slug}
                  onChange={handleChange('slug')}
                  placeholder="acme-inc"
                  helperText="Your organization's unique identifier"
                  disabled={organization?.isPersonal}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  multiline
                  rows={3}
                  placeholder="Tell us about your organization..."
                  disabled={organization?.isPersonal}
                  data-grid-span={12}
                />
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={handleChange('website')}
                  type="url"
                  placeholder="https://example.com"
                  disabled={organization?.isPersonal}
                />
                <TextField
                  fullWidth
                  label="Industry"
                  value={formData.industry}
                  onChange={handleChange('industry')}
                  placeholder="Technology, Finance, Healthcare..."
                  disabled={organization?.isPersonal}
                />
                <TextField
                  fullWidth
                  label="Company Size"
                  value={formData.size}
                  onChange={handleChange('size')}
                  placeholder="1-10, 11-50, 51-200..."
                  disabled={organization?.isPersonal}
                />
              </GridLayout>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  loading={saving}
                  startIcon={<SaveIcon sx={{ color: 'var(--theme-on-primary)' }} />}
                  disabled
                >
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Text variant="subtitle2" style={{ marginTop: '16px' }} customColor="var(--theme-text)">
            Need organization features? Contact sales or upgrade your plan to get started.
          </Text>
        </>
      )}
    </Box>
  );
}
